import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import Typography from '@navi/web-ui/lib/primitives/Typography/Typography';
import { Popper, PopperContent, PopperTrigger } from '@cp/src/components/Popper/Popper';
import PhoneCallIcon from '@cp/src/assets/icons/PhoneCallIcon';
import { RootState } from '@cp/src/store';
import { DetailsPopperProps, CallingAgentAuditDetails } from '../../interface';
import AnswerViewsSection from './AnswerViewsSection';
import CallSummarySection from './CallSummarySection';
import AuditDetailsSection from './AuditDetailsSection';
import AiAuditSummarySection from './AiAuditSummarySection';
import { CALL_TYPE_LABELS } from '../constant';
import { FEEDBACK_TYPES_OPTIONS } from '@cp/src/pages/Cases/constants/CasesConstants';
import ChevronLeft from '@cp/src/assets/icons/ChevronLeft';

const DetailsPopper = ({ isTDME = false, isDrawerOpen = true }: DetailsPopperProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const auditDetails = useSelector(
    (state: RootState) => state.callAudit?.callingAgentAuditDetails
  ) as CallingAgentAuditDetails | null;

  const callType = CALL_TYPE_LABELS[auditDetails?.callType || ''] || auditDetails?.callType || '';
  const callLanguage = auditDetails?.callLanguage || '';
  const tag = auditDetails?.tag || '';
  const callSummary = auditDetails?.callSummary;

  const callDisplayData = useMemo(
    () => ({
      callType: `${callType}${callLanguage ? `, ${callLanguage}` : ''}`,
      status: tag
    }),
    [callType, callLanguage, tag]
  );

  const answerViews = callSummary?.answerViews || [];

  const handleClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (isTDME && !isDrawerOpen) {
      setIsOpen(false);
    }
  }, [isTDME, isDrawerOpen]);

  return (
    <div className="relative">
      <Popper
        placement="left-start"
        open={isOpen}
        onOpenChange={setIsOpen}
        disableCloseOnOutsideClick={true}
      >
        <PopperTrigger>
          <div className="flex flex-row cursor-pointer">
            <div className="bg-[var(--bg-dark)] flex items-center justify-center w-[24px] h-[102px] shadow-md">
              <ChevronLeft />
            </div>
            <div className="flex-1 flex flex-row items-center justify-between px-3 pt-2 pb-3 bg-[var(--bg-primary-warm)]">
              <div className="flex flex-col">
                <Typography variant="p4" color="var(--grayscale-3)">
                  Call Type
                </Typography>
                <Typography variant="p4" color="var(--grayscale-1)">
                  {callDisplayData?.callType || '-'}
                </Typography>
              </div>

              <div className="flex flex-col">
                <div>
                  <Typography variant="p4" color="var(--grayscale-3)">
                    Status
                  </Typography>
                  <Typography variant="p4" color="var(--grayscale-1)">
                    {FEEDBACK_TYPES_OPTIONS.find(option => option.value === callDisplayData?.status)
                      ?.label ||
                      callDisplayData?.status ||
                      '-'}
                  </Typography>
                </div>
                <div>
                  <Typography variant="p5" color="var(--grayscale-3)">
                    Customer
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        </PopperTrigger>
        <PopperContent
          className="max-w-[90vw] w-[360px] rounded-xl !left-2 !top-[-10px] bg-[var(--bg-primary)] shadow-lg z-[var(--z-index-overlay)] !visible"
          showArrow={false}
          headerClassName="!bg-[var(--bg-dark)] text-[var(--bg-primary)] flex flex-row justify-start gap-2 rounded-t-lg"
          headerText="Call summary"
          headerIcon={<PhoneCallIcon fillColor="var(--bg-dark)" width={20} height={20} />}
        >
          <div className="flex flex-col max-h-[calc(100vh-120px)] rounded-xl">
            <div className="flex-shrink-0">
              <AnswerViewsSection answerViews={answerViews} onClose={handleClose} />
              <CallSummarySection callSummary={callSummary} />
              <AuditDetailsSection callSummary={callSummary} isTDME={isTDME} />
            </div>
            <AiAuditSummarySection violations={callSummary?.genAiCocViolations || []} />
          </div>
        </PopperContent>
      </Popper>
    </div>
  );
};

export default DetailsPopper;
